import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLDomainSelect } from '../components/SMLDomainSelect/SMLDomainSelect';
import { splitPlatformDomainPrefix } from '../utils/platformDomainPrefix';
import { CreationSlug, SlugState } from './useCreationSlug';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the sheet; each part uses its own slice of it */
import styles from './SMLCreationSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CreationSlugFieldProps {
  slug: CreationSlug;
  /** The page's public host, ending in a slash. */
  prefix: string;
  /** The host in that prefix, so the select can show which one is chosen. */
  platformDomain: string;
  onPlatformDomainChange: (platformDomain: string) => void;
  disabled?: boolean;
}

/** The page address, decided here rather than discovered afterwards. */
const CreationSlugFieldComponent: React.FC<CreationSlugFieldProps> = ({
  slug,
  prefix,
  platformDomain,
  onPlatformDomainChange,
  disabled,
}) => {
  const intl = useIntl();

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => slug.setValue(event.target.value),
    [slug]
  );

  // Only an address on a platform host can have its host picked. A local one
  // and a custom domain stay the plain string the server built.
  const prefixParts = useMemo(() => splitPlatformDomainPrefix(prefix), [prefix]);

  const isBad = slug.state === SlugState.taken || slug.state === SlugState.illegal;

  return (
    <div className={clsx(styles.field, styles.fieldWide)}>
      <span className={styles.fieldLabel}>
        {intl.formatMessage({ id: 'app.sml.create.slug.label', defaultMessage: 'Page address' })}
      </span>
      <div className={clsx(styles.fieldBox, isBad && styles.fieldBoxInvalid)}>
        {prefixParts === null ? (
          <span className={styles.fieldPrefix}>{prefix}</span>
        ) : (
          <>
            <span className={styles.fieldPrefix}>{prefixParts.head}</span>
            <SMLDomainSelect value={platformDomain} onChange={onPlatformDomainChange} disabled={disabled} />
            <span className={styles.fieldPrefix}>{prefixParts.tail}</span>
          </>
        )}
        <input
          className={clsx(styles.fieldInput, styles.fieldInputMono)}
          value={slug.value}
          disabled={disabled}
          spellCheck={false}
          onChange={onChange}
          aria-label={intl.formatMessage({ id: 'app.sml.create.slug.label', defaultMessage: 'Page address' })}
        />
      </div>
      <span className={clsx(styles.fieldHelp, isBad && styles.fieldHelpBad)}>
        {slug.state === SlugState.taken &&
          intl.formatMessage(
            {
              id: 'app.sml.create.slug.taken',
              defaultMessage: 'One of your pages already answers to this. {suggestion} is free.',
            },
            { suggestion: slug.suggestion ?? '' }
          )}
        {slug.state === SlugState.illegal &&
          intl.formatMessage(
            {
              id: 'app.sml.create.slug.illegal',
              defaultMessage: 'An address takes letters, numbers and dashes. {suggestion} works.',
            },
            { suggestion: slug.suggestion ?? '' }
          )}
        {slug.state !== SlugState.taken &&
          slug.state !== SlugState.illegal &&
          intl.formatMessage({
            id: 'app.sml.create.slug.help',
            defaultMessage: 'Changeable until the page is published.',
          })}
      </span>
    </div>
  );
};

export const CreationSlugField = TypedMemo(CreationSlugFieldComponent);
